import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  // Handle scroll to section after navigation
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  const handleNavClick = (sectionId) => {
    setMobileMenuOpen(false);
    
    if (isHomePage) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(`/#${sectionId}`);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a1c1b]/95 backdrop-blur-sm border-b border-[#3f4816]/50">
      <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="text-[#d9fb06] font-bold text-2xl tracking-tight hover:opacity-80 transition-opacity">
              validatedboat
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-3 xl:space-x-5 whitespace-nowrap">
            <Link 
              to="/bateaux-a-vendre"
              data-testid="nav-bateaux"
              className="text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium text-sm"
            >
              Bateaux sélectionnés
            </Link>
            <button 
              onClick={() => handleNavClick('services')} 
              className="text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium text-sm"
            >
              Services
            </button>
            <button 
              onClick={() => handleNavClick('pricing')} 
              className="text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium text-sm"
            >
              Tarifs
            </button>
            <Link 
              to="/blog" 
              className="text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium text-sm"
            >
              Blog
            </Link>
            <Link 
              to="/vie-a-bord" 
              className="text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium text-sm"
            >
              Vie à bord
            </Link>
            <Link 
              to="/vendre-mon-bateau"
              data-testid="nav-vendre"
              className="text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium text-sm"
            >
              Vendre mon bateau
            </Link>
            <Link 
              to="/transmettre-son-bateau"
              data-testid="nav-transmettre"
              className="text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium text-sm"
            >
              Transmettre son bateau
            </Link>
            <Button 
              asChild
              data-testid="nav-cta-btn"
              className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-4 py-2 text-sm font-semibold transition-all hover:scale-105"
            >
              <Link to="/analyse-annonce-bateau" data-testid="nav-analyse-annonce">Faire analyser une annonce</Link>
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-[#d9fb06] p-2"
            aria-label="Menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 space-y-2 border-t border-[#3f4816]/50">
            <Link 
              to="/analyse-annonce-bateau"
              className="block w-full text-left text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium py-3 px-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Analyser une annonce
            </Link>
            <Link 
              to="/bateaux-a-vendre"
              data-testid="nav-bateaux-mobile"
              className="block w-full text-left text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium py-3 px-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Bateaux sélectionnés
            </Link>
            <button 
              onClick={() => handleNavClick('services')} 
              className="block w-full text-left text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium py-3 px-2"
            >
              Services
            </button>
            <button 
              onClick={() => handleNavClick('pricing')} 
              className="block w-full text-left text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium py-3 px-2"
            >
              Tarifs
            </button>
            <Link 
              to="/blog" 
              className="block w-full text-left text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium py-3 px-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Blog
            </Link>
            <Link 
              to="/vie-a-bord" 
              className="block w-full text-left text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium py-3 px-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Vie à bord
            </Link>
            <Link 
              to="/vendre-mon-bateau" 
              className="block w-full text-left text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium py-3 px-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Vendre mon bateau
            </Link>
            <Link 
              to="/transmettre-son-bateau" 
              data-testid="nav-transmettre-mobile"
              className="block w-full text-left text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium py-3 px-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Transmettre son bateau
            </Link>
            <div className="pt-2">
              <Button asChild className="w-full bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-6 py-3 font-semibold">
                <Link to="/analyse-annonce-bateau" onClick={() => setMobileMenuOpen(false)}>
                  Faire analyser une annonce
                </Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
